/*
 This file is part of GNU Taler
 (C) 2019 GNUnet e.V.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { BitcoinBech32 } from "./bech32.js";
import { generateFakeSegwitAddress } from "./bitcoin.js";
import { Codec, Context, DecodingError, renderContext } from "./codec.js";
import { assertUnreachable, TalerError } from "./errors.js";
import { IbanString, parseIban, ParseIbanError } from "./iban.js";
import { j2s } from "./helpers.js";
import { Logger } from "./logging.js";
import { Result, ResultError, ResultOk } from "./result.js";
import { BitcoinSewgit } from "./segwit_addr.js";
import {
  decodeCrock,
  encodeCrock,
  hashTruncate32,
  stringToBytes,
} from "./taler-crypto.js";
import { TalerErrorCode } from "./taler-error-codes.js";
import { AmountString } from "./types-taler-common.js";
import { TransferSubject } from "./types-taler-prepared-transfer.js";
import { URLSearchParams } from "./url.js";

const logger = new Logger("payto.ts");

export type PaytoString = string;

const PAYTO_PREFIX = "payto://";

export enum PaytoType {
  IBAN = "iban",
  Bitcoin = "bitcoin",
  Cyclos = "cyclos",
  TalerBank = "x-taler-bank",
  TalerReserve = "taler-reserve",
  TalerReserveHttp = "taler-reserve-http",
  Ethereum = "ethereum",
  Void = "void",
}

export enum ReservePubParseError {
  /**
   * It should be 52 characters
   */
  WRONG_LENGTH,
  DECODE_ERROR,
}

export interface PaytoInputFields {
  targetType: "iban" | "x-taler-bank" | "upi" | "bitcoin" | string;
  iban?: string;
  bankHost?: string;
  accountName?: string;
  accountHolder?: string;
}
declare const __hostport_str: unique symbol;
export type HostPortPath = string & { [__hostport_str]: true };

declare const __btaddr_str: unique symbol;
export type BtAddrString = string & { [__btaddr_str]: true };
declare const __ethaddr_str: unique symbol;
export type EthAddrString = string & { [__ethaddr_str]: true };

export enum PaytoParseError {
  /**
   * Payto should start with payto://
   */
  WRONG_PREFIX,
  /**
   * Payto should have a / after the target type
   */
  INCOMPLETE,
  /**
   * Target type is not in the list of supported types
   */
  UNSUPPORTED,
  /**
   * The quantity of components is wrong based on the target type
   */
  COMPONENTS_LENGTH,
  /**
   * The validation of one or more path components failed
   */
  INVALID_TARGET_PATH,
}

// TODO: use unique symbol to remove compat with string
// TODO: verify in account aml types if we can merge it since this is also an aml account
export type PaytoHash = string;

export namespace Paytos {
  export type URI =
    | PaytoUnsupported
    | PaytoIBAN
    | PaytoTalerReserve
    | PaytoCyclos
    | PaytoTalerReserveHttp
    | PaytoTalerBank
    | PaytoEthereum
    | PaytoBitcoin
    | PaytoVoid;

  declare const __full_payto_str: unique symbol;
  export type FullPaytoString = string & { [__full_payto_str]: true };

  declare const __norm_payto_str: unique symbol;
  export type NormalizedPaytoString = string & { [__norm_payto_str]: true };

  interface PaytoGeneric {
    /**
     * String after the prefix and before the first /
     */
    targetType: PaytoType | undefined;
    /**
     * String after the first /
     */
    normalizedPath: string;
    /**
     * String after the first /
     */
    fullPath: string;
    /**
     * Return the account identification when the target type is already known. Useful to show in the UI
     */
    displayName: string;
    /**
     * All the URL params after the first ?
     */
    params: { [name: string]: string };
  }

  export interface PaytoUnsupported extends PaytoGeneric {
    targetType: undefined;
    target: string;
  }

  export interface PaytoIBAN extends PaytoGeneric {
    targetType: PaytoType.IBAN;
    iban: IbanString;
    bic?: string;
  }

  export interface PaytoTalerReserve extends PaytoGeneric {
    targetType: PaytoType.TalerReserve;
    exchange: HostPortPath;
    reservePub: Uint8Array;
  }

  export interface PaytoCyclos extends PaytoGeneric {
    targetType: PaytoType.Cyclos;
    url: HostPortPath;
    account: string;
  }

  export interface PaytoTalerReserveHttp extends PaytoGeneric {
    targetType: PaytoType.TalerReserveHttp;
    exchange: HostPortPath;
    reservePub: Uint8Array;
  }

  export interface PaytoTalerBank extends PaytoGeneric {
    targetType: PaytoType.TalerBank;
    /**
     * this is kept to keep compatibility with old parser
     * @deprecated use URL
     */
    host: string;
    url: HostPortPath;
    account: string;
  }

  export interface PaytoVoid extends PaytoGeneric {
    targetType: PaytoType.Void;
  }

  export interface PaytoBitcoin extends PaytoGeneric {
    targetType: PaytoType.Bitcoin;
    address: BtAddrString;
    reservePub: Uint8Array | undefined;
    segwitAddrs: Array<BtAddrString>;
  }

  export interface PaytoEthereum extends PaytoGeneric {
    targetType: PaytoType.Ethereum;
    address: EthAddrString;
  }

  const supportedTargets: Record<PaytoType, true> = {
    iban: true,
    bitcoin: true,
    "x-taler-bank": true,
    "taler-reserve": true,
    "taler-reserve-http": true,
    ethereum: true,
    cyclos: true,
    void: true,
  };

  export function hashFull(
    p: NormalizedPaytoString | FullPaytoString | string,
  ): Uint8Array {
    return hashTruncate32(stringToBytes(p + "\0"));
  }

  /**
   * Parse, normalize and hash a full payto-URI into its canonical H_PAYTO.
   *
   * Shorthand for `hash(toNormalizedString(asString(p)))`.  Throws if `p` is
   * not a valid, supported payto-URI.
   */
  export function hashNormalized(p: FullPaytoString | string): Uint8Array {
    return hashFull(toNormalizedString(asString(p as FullPaytoString)));
  }

  /**
   * A **normalized** payto-URI uniquely identifies a bank account (or
   * wallet) and must be able to serve as a canonical representation of such a
   * bank account.  Thus, optional arguments such as the *receiver-name* or
   * optional path components such as the BIC must be removed and the account
   * must be given in a canonical form for the wire method (for example,
   * everything in lower-case)
   *
   * @param p
   * @returns
   */
  export function toNormalizedString(p: URI): NormalizedPaytoString {
    // An unsupported target type keeps the raw value in `target`, since it is
    // by definition not a member of PaytoType.
    const target = p.targetType ?? (p as PaytoUnsupported).target;
    // Concatenate directly rather than via a URL parser: normalizedPath is
    // already canonical, and passing it through `new URL` would resolve dot
    // segments, diverging from the C normalizer and thus from H_PAYTO.
    return `${PAYTO_PREFIX}${target}/${p.normalizedPath}` as NormalizedPaytoString;
  }
  /**
   * A **full** payto-URI is not expected to have a canonical form for
   * a bank account (there can be many full payto-URIs for the same bank
   * account) and must include at least the *receiver-name* but possibly also
   * other (in RFC 8905 optional) arguments to identify the recipient, as
   * those may be needed to do a wire transfer.
   *
   * @param p
   * @returns
   */
  export function toFullString(p: URI): FullPaytoString {
    const target = p.targetType ?? (p as PaytoUnsupported).target;
    const paramList = !p.params ? [] : Object.entries(p.params);
    const search = createSearchParams(paramList);
    // Concatenate directly rather than via a URL parser, as
    // toNormalizedString does: `new URL` resolves dot segments, strips tab,
    // LF and CR and re-encodes the query, so the full payto would stop being
    // the string TALER_full_payto_hash covers.
    const query = search ? `?${search}` : "";
    return `${PAYTO_PREFIX}${target}/${p.fullPath}${query}` as FullPaytoString;
  }

  export function parseReservePub(
    reserve: string | undefined,
  ):
    | ResultOk<Uint8Array>
    | ResultError<ReservePubParseError, { message: string } | undefined> {
    if (!reserve) return Result.error(ReservePubParseError.WRONG_LENGTH);
    try {
      const pub = decodeCrock(reserve);
      if (!pub || pub.length !== 32) {
        return Result.error(ReservePubParseError.WRONG_LENGTH);
      }
      return Result.of(pub);
    } catch (e) {
      return Result.errorWithDetail(ReservePubParseError.DECODE_ERROR, {
        message: String(e),
      });
    }
  }
  /**
   * Check hostname is a valid string with form $host:$port/$path like
   * domain.com:22/some/path
   *
   * Return the canonical form.
   *
   * FIXME: new need a function that only takes una string: host+port+path and
   * parse it without using URL to prevent parsing unnecessary components and
   * better error reporting
   * https://bugs.gnunet.org/view.php?id=10467
   *
   * @param hostname
   * @param path
   * @param scheme
   * @returns
   */
  export function parseHostPortPath2(
    hostname: string,
    path: string | undefined,
    scheme: "http" | "https" = "https",
  ): HostPortPath | undefined {
    // maybe it should check that it doesn't contain search or hash?
    try {
      // https://url.spec.whatwg.org/#concept-basic-url-parser
      if (path === undefined) {
        path = "";
      }
      if (!path.endsWith("/")) {
        path = path + "/";
      }
      const url = new URL(path, `${scheme}://${hostname.toLowerCase()}`);
      url.search = "";
      url.password = "";
      url.username = "";
      url.hash = "";
      return url.href as HostPortPath;
    } catch {
      return undefined;
    }
  }

  function withoutScheme(h: HostPortPath): HostPortPath {
    return (
      h.startsWith("http://")
        ? h.substring(7)
        : h.startsWith("https://")
          ? h.substring(8)
          : h
    ) as HostPortPath;
  }

  /**
   * Same as `parseHostPortPath2` but only takes one string.
   * This should be the definitive signature.
   * https://bugs.gnunet.org/view.php?id=10467
   *
   * @param hostnameAndPath
   * @returns
   */
  export function parseHostPortPath(
    hostnameAndPath: string,
  ): HostPortPath | undefined {
    const slashIdx = hostnameAndPath.indexOf("/");
    const host =
      slashIdx < 0 ? hostnameAndPath : hostnameAndPath.slice(0, slashIdx);
    const path = slashIdx < 0 ? "" : hostnameAndPath.slice(slashIdx + 1);
    return parseHostPortPath2(host, path);
  }

  /**
   * @param str
   */
  export function parseEthereumAddress(str: string): EthAddrString | undefined {
    if (!str) {
      return undefined;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(str as string)) {
      return undefined;
    }
    return str as EthAddrString;
  }

  /**
   * FIXME: add bank account name validation
   *
   * @param account
   * @returns
   */
  export function parseTalerBankAccount(account: string): string | undefined {
    if (!account) {
      return undefined;
    }
    return account;
  }

  //////////////////
  // function to create objs
  //////////////////
  export function createUnsupported(
    targetType: string,
    path: string,
    params: Record<string, string> = {},
  ): PaytoUnsupported {
    return {
      targetType: undefined,
      target: targetType,
      params,
      normalizedPath: path.toLowerCase(),
      fullPath: path,
      displayName: path,
    };
  }

  export function createIban(
    iban: IbanString,
    bic: string | undefined,
    params: Record<string, string> = {},
  ): PaytoIBAN {
    iban = iban.toUpperCase() as IbanString;
    return {
      targetType: PaytoType.IBAN,
      iban,
      bic,
      params,
      normalizedPath: iban,
      fullPath: !bic ? iban : `${bic}/${iban}`,
      displayName: iban,
    };
  }

  export function createBitcoin(
    address: BtAddrString,
    reservePub: Uint8Array | undefined,
    params: Record<string, string> = {},
  ): PaytoBitcoin {
    const sgRes = !reservePub
      ? undefined
      : generateFakeSegwitAddress(reservePub, address);

    const segwitAddrs = !sgRes || !Result.isOk(sgRes) ? [] : sgRes.value;
    return {
      targetType: PaytoType.Bitcoin,
      address,
      reservePub,
      segwitAddrs,
      params,
      normalizedPath: address.toLowerCase(),
      fullPath: !reservePub ? address : `${address}/${encodeCrock(reservePub)}`,
      displayName: address,
    };
  }

  export function createEthereum(
    address: EthAddrString,
    params: Record<string, string> = {},
  ): PaytoEthereum {
    return {
      targetType: PaytoType.Ethereum,
      address,
      params,
      normalizedPath: address,
      fullPath: address,
      displayName: address,
    };
  }

  export function createTalerReserve(
    exchange: HostPortPath,
    reservePub: Uint8Array,
    params: Record<string, string> = {},
  ): PaytoTalerReserve {
    const path = withoutScheme(exchange);
    const pub = encodeCrock(reservePub);
    return {
      targetType: PaytoType.TalerReserve,
      exchange,
      reservePub,
      params,
      normalizedPath: `${path.toLowerCase()}${pub}`,
      fullPath: `${path}${pub}`,
      displayName: `${path}@${pub}`,
    };
  }

  export function createCyclos(
    url: HostPortPath,
    account: string,
    params: Record<string, string> = {},
  ): PaytoCyclos {
    const path = withoutScheme(url);
    return {
      targetType: PaytoType.Cyclos,
      url,
      account,
      params,
      normalizedPath: `${path.toLowerCase()}${account}`,
      fullPath: `${path}${account}`,
      displayName: `${account}@${path}`,
    };
  }

  export function createTalerReserveHttp(
    exchange: HostPortPath,
    reservePub: Uint8Array,
    params: Record<string, string> = {},
  ): PaytoTalerReserveHttp {
    const path = withoutScheme(exchange);
    const pub = encodeCrock(reservePub);
    return {
      targetType: PaytoType.TalerReserveHttp,
      exchange,
      reservePub,
      params,
      normalizedPath: `${path.toLowerCase()}${pub}`,
      fullPath: `${path}${pub}`,
      displayName: `${path}@${pub}`,
    };
  }

  export function createTalerBank(
    url: HostPortPath,
    account: string,
    params: Record<string, string> = {},
    rawSegments?: string[],
  ): PaytoTalerBank {
    const path = withoutScheme(url);
    const host = path.endsWith("/") ? path.substring(0, path.length - 1) : path;
    // Match normalize_payto_x_taler_bank in the C exchange: lowercase only the
    // host, preserve the case of the account path, and do not resolve dot
    // segments.  Both forms are built from the raw segments so that going
    // through a URL parser (which would lowercase or resolve) cannot change
    // H_PAYTO.
    const segments = rawSegments ?? [host, account];
    const normalizedPath = `${segments[0].toLowerCase()}/${segments
      .slice(1)
      .join("/")}`;
    const fullPath = segments.join("/");
    return {
      targetType: PaytoType.TalerBank,
      host,
      url,
      account,
      params,
      normalizedPath,
      fullPath,
      displayName: `${account} @ ${host}`,
    };
  }

  export function createPaytoVoid(
    voidPath: string,
    params: Record<string, string> = {},
  ): PaytoVoid {
    return {
      targetType: PaytoType.Void,
      params,
      normalizedPath: voidPath,
      fullPath: voidPath,
      displayName: `void(${voidPath})`,
    };
  }

  //////////////////////
  // parsing function
  ///////////////////////

  export function asString(p: FullPaytoString): Paytos.URI {
    return Result.unpack(fromString(p));
  }

  /**
   * Parse a payto:// URI that came from outside (a client request, a server
   * response, the database), and raise a coded error if it does not parse.
   *
   * Callers that already know the URI is well-formed should use
   * {@link asString}; this one keeps the reason for the rejection in the hint
   * so that a UI can tell the user what is actually wrong with the account.
   */
  export function parseOrThrow(
    s: string,
    opts: ParsePaytoOptions = {},
  ): Paytos.URI {
    const res = fromString(s, opts);
    if (Result.isError(res)) {
      throw TalerError.fromDetail(
        TalerErrorCode.GENERIC_PAYTO_URI_MALFORMED,
        {},
        `payto URI is malformed (${PaytoParseError[res.error]})`,
      );
    }
    return res.value;
  }

  export interface ParsePaytoOptions {
    /**
     * do not check path component format
     */
    ignoreComponentError?: boolean;
    /**
     * take unknown target types as valid
     */
    allowUnsupported?: boolean;
  }

  export type TargetPathErrorDetail =
    | {
        targetType: PaytoType.Bitcoin;
        pos: 0;
        error: ResultError<
          | BitcoinBech32.BitcoinParseError
          | BitcoinSewgit.BitcoinSewgitParseError
        >;
      }
    | {
        targetType: PaytoType.Bitcoin;
        pos: 1;
        error: ResultError<
          ReservePubParseError,
          { message: string } | undefined
        >;
      }
    | {
        targetType: PaytoType.IBAN;
        pos: 0 | 1;
        error: ResultError<ParseIbanError>;
      }
    | {
        targetType: PaytoType.TalerBank;
        pos: 0 | 1;
      }
    | {
        targetType: PaytoType.TalerReserve;
        pos: 0;
      }
    | {
        targetType: PaytoType.TalerReserve;
        pos: 1;
        error: ResultError<
          ReservePubParseError,
          { message: string } | undefined
        >;
      }
    | {
        targetType: PaytoType.TalerReserveHttp;
        pos: 0;
      }
    | {
        targetType: PaytoType.TalerReserveHttp;
        pos: 1;
        error: ResultError<
          ReservePubParseError,
          { message: string } | undefined
        >;
      }
    | {
        targetType: PaytoType.Ethereum;
        pos: 0;
      }
    | {
        targetType: PaytoType.Cyclos;
        pos: 0;
      };

  export function fromString(
    s: string,
    opts: ParsePaytoOptions = {},
  ):
    | ResultOk<Paytos.URI>
    | ResultError<PaytoParseError.WRONG_PREFIX>
    | ResultError<PaytoParseError.UNSUPPORTED, { targetType: string }>
    | ResultError<PaytoParseError.COMPONENTS_LENGTH, { targetType: PaytoType }>
    | ResultError<PaytoParseError.INVALID_TARGET_PATH, TargetPathErrorDetail>
    | ResultError<PaytoParseError.INCOMPLETE, { targetType: string }> {
    // The scheme and the target type are case insensitive, as in the C
    // reference (strncasecmp on the prefix, strcasecmp on the method).
    if (s.slice(0, PAYTO_PREFIX.length).toLowerCase() !== PAYTO_PREFIX) {
      return Result.error(PaytoParseError.WRONG_PREFIX);
    }

    // RFC 3986 section 3.4: "?" is legal inside the query.
    const rest = s.slice(PAYTO_PREFIX.length);
    const queryPos = rest.indexOf("?");
    const acct = queryPos === -1 ? rest : rest.slice(0, queryPos);
    const search = queryPos === -1 ? undefined : rest.slice(queryPos + 1);

    const firstSlashPos = acct.indexOf("/");

    const rawTargetType =
      firstSlashPos === -1 ? acct : acct.slice(0, firstSlashPos);
    const targetType = rawTargetType.toLowerCase() as PaytoType;
    if (
      !opts.allowUnsupported &&
      !Object.prototype.hasOwnProperty.call(supportedTargets, targetType)
    ) {
      return Result.errorWithDetail(PaytoParseError.UNSUPPORTED, {
        targetType,
      });
    }
    const targetPath = acct.slice(firstSlashPos + 1);
    if (firstSlashPos === -1 || !targetPath) {
      return Result.errorWithDetail(PaytoParseError.INCOMPLETE, { targetType });
    }

    const params: { [k: string]: string } = {};
    if (search) {
      const searchParams = new URLSearchParams(search);
      searchParams.forEach((v, k) => {
        // URLSearchParams already decodes uri components
        params[k] = v;
      });
    }
    // get URI components
    const cs = targetPath.split("/");
    switch (targetType) {
      case PaytoType.IBAN: {
        if (cs.length !== 1 && cs.length !== 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }
        const bic = cs.length === 2 ? cs[0] : undefined;
        const iban = cs.length === 1 ? cs[0] : cs[1];

        const ibaRes = parseIban(iban);

        if (!opts.ignoreComponentError && ibaRes.tag === "error") {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0,
            targetType,
            error: ibaRes,
          } as const);
        }

        // Use the canonical form, so that H_PAYTO does not depend on how
        // the IBAN was written.
        const canonicalIban =
          ibaRes.tag === "ok" ? Result.unpack(ibaRes) : (iban as IbanString);
        return Result.of(createIban(canonicalIban, bic, params));
      }
      case PaytoType.Bitcoin: {
        if (cs.length !== 1 && cs.length !== 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }

        const address = cs[0].toLowerCase();
        // Validate as a segwit address rather than as a bare bech32 string:
        // the witness version decides which checksum constant applies, so
        // pinning bech32 here rejects taproot and every later version.
        const btRes = BitcoinSewgit.decode(address);
        if (!opts.ignoreComponentError && Result.isError(btRes)) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0 as const,
            targetType,
            error: btRes,
          });
        }

        const pubRes = cs.length === 1 ? undefined : parseReservePub(cs[1]);
        if (!opts.ignoreComponentError && pubRes && Result.isError(pubRes)) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 1 as const,
            targetType,
            error: pubRes,
          });
        }

        return Result.of(
          createBitcoin(
            address as BtAddrString,
            pubRes != null ? Result.orUndefined(pubRes) : undefined,
            params,
          ),
        );
      }

      case PaytoType.TalerBank: {
        if (cs.length < 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }

        const host = parseHostPortPath2(cs[0], cs.slice(1, -1).join("/"));
        if (!opts.ignoreComponentError && !host) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0,
            targetType,
            error: host,
          } as const);
        }
        const account = parseTalerBankAccount(cs[cs.length - 1]);
        if (!opts.ignoreComponentError && !account) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 1 as const,
            targetType,
            error: account,
          });
        }

        return Result.of(
          createTalerBank(
            host ?? (cs[0] as HostPortPath),
            account ?? cs[cs.length - 1],
            params,
            cs,
          ),
        );
      }
      case PaytoType.TalerReserve: {
        if (cs.length < 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }
        const exchange = parseHostPortPath2(cs[0], cs.slice(1, -1).join("/"));
        if (!opts.ignoreComponentError && !exchange) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0 as const,
            targetType,
            error: exchange,
          });
        }

        const reservePub = cs[cs.length - 1];
        const pubRes = parseReservePub(reservePub);
        if (!opts.ignoreComponentError && !Result.isOk(pubRes)) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 1 as const,
            targetType,
            error: pubRes,
          });
        }

        return Result.of<URI>(
          createTalerReserve(
            exchange ?? (cs[0] as HostPortPath),
            Result.isOk(pubRes) ? pubRes.value : new Uint8Array(32),
            params,
          ),
        );
      }
      case PaytoType.TalerReserveHttp: {
        if (cs.length < 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }
        const exchange = parseHostPortPath2(
          cs[0],
          cs.slice(1, -1).join("/"),
          "http",
        );
        if (!opts.ignoreComponentError && !exchange) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0,
            targetType,
            error: exchange,
          } as const);
        }

        const reservePub = cs[cs.length - 1];
        const pubRes = parseReservePub(reservePub);
        if (!opts.ignoreComponentError && !Result.isOk(pubRes)) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 1,
            targetType,
            error: pubRes,
          } as const);
        }
        return Result.of<URI>(
          createTalerReserveHttp(
            exchange ?? (cs[0] as HostPortPath),
            Result.isOk(pubRes) ? pubRes.value : new Uint8Array(32),
            params,
          ),
        );
      }
      case PaytoType.Ethereum: {
        if (cs.length !== 1) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }
        const address = parseEthereumAddress(cs[0]);
        if (!opts.ignoreComponentError && !address) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0,
            targetType,
            error: address,
          } as const);
        }
        return Result.of<URI>(
          createEthereum(address ?? (cs[0] as EthAddrString), params),
        );
      }
      case PaytoType.Cyclos: {
        if (cs.length < 2) {
          return Result.errorWithDetail(PaytoParseError.COMPONENTS_LENGTH, {
            targetType,
          });
        }
        const host = parseHostPortPath2(cs[0], cs.slice(1, -1).join("/"));
        if (!opts.ignoreComponentError && !host) {
          return Result.errorWithDetail(PaytoParseError.INVALID_TARGET_PATH, {
            pos: 0 as const,
            targetType,
            error: host,
          });
        }

        const accountId = cs[cs.length - 1];

        return Result.of<URI>(
          createCyclos(host ?? (cs[0] as HostPortPath), accountId, params),
        );
      }
      case PaytoType.Void: {
        return Result.of<URI>(createPaytoVoid(targetPath, params));
      }
      default: {
        if (opts.allowUnsupported) {
          return Result.of<URI>(
            createUnsupported(rawTargetType, targetPath, params),
          );
        }
        assertUnreachable(targetType);
      }
    }
  }

  export function getAccountHolder(
    uri: URI | undefined | null,
  ): string | undefined {
    if (!uri || !uri.params) return undefined;
    return (
      uri.params["receiver-name"] ||
      uri.params["receiver"] ||
      uri.params["account-name"] ||
      uri.params["name"] ||
      undefined
    );
  }

  export function getReceiverPostalCode(
    uri: URI | undefined | null,
  ): string | undefined {
    if (!uri || !uri.params) return undefined;
    return (
      uri.params["receiver-postal-code"] ||
      uri.params["receiver-postcode"] ||
      uri.params["postal-code"] ||
      uri.params["postcode"] ||
      undefined
    );
  }

  export function getReceiverTown(
    uri: URI | undefined | null,
  ): string | undefined {
    if (!uri || !uri.params) return undefined;
    return (
      uri.params["receiver-town"] ||
      uri.params["receiver-city"] ||
      uri.params["town"] ||
      uri.params["city"] ||
      undefined
    );
  }

  export function getAccountNumber(
    uri: URI | undefined | null,
  ): string | undefined {
    if (!uri) return undefined;
    if (uri.targetType === PaytoType.IBAN) {
      const rawIban = uri.iban || "";
      const formattedIban = rawIban.replace(/(.{4})/g, "$1 ").trim();
      return formattedIban || rawIban;
    }
    if (uri.targetType === PaytoType.TalerBank) {
      return uri.account;
    }
    return uri.fullPath;
  }

  export function getBic(uri: URI | undefined | null): string | undefined {
    if (!uri) return undefined;
    if (uri.targetType === PaytoType.IBAN) {
      return uri.bic || uri.params["bic"];
    }
    return uri.params["bic"];
  }

  export function getBankHost(uri: URI | undefined | null): string | undefined {
    if (!uri) return undefined;
    if (uri.targetType === PaytoType.TalerBank) {
      return uri.host;
    }
    return undefined;
  }

  export function getTargetLabel(uri: URI | undefined | null): string {
    if (!uri) return "Payout Account";
    if (uri.targetType === PaytoType.IBAN) return "IBAN";
    if (uri.targetType === PaytoType.TalerBank) return "Taler Bank";
    const target = uri.targetType ?? (uri as PaytoUnsupported).target;
    return target ? target.toUpperCase() : "Payout Account";
  }

  export function parsePaytoUri(rawUri?: string): URI | undefined {
    if (!rawUri || !rawUri.trim()) return undefined;
    const trimmed = rawUri.trim();
    const res = fromString(trimmed, { allowUnsupported: true });
    if (Result.isOk(res)) {
      return res.value;
    }
    if (/^[A-Z]{2}[0-9]{2}/i.test(trimmed)) {
      const ibanRes = parseIban(trimmed);
      const canonicalIban = Result.isOk(ibanRes)
        ? ibanRes.value
        : (trimmed.replace(/\s+/g, "").toUpperCase() as IbanString);
      return createIban(canonicalIban, undefined);
    }
    return createUnsupported("account", trimmed);
  }

  export function constructPayto(fields: PaytoInputFields): string {
    const holder = fields.accountHolder?.trim();
    const search: Record<string, string> = holder
      ? { "receiver-name": holder }
      : {};

    if (fields.targetType === PaytoType.IBAN || fields.targetType === "iban") {
      const cleanIban = (fields.iban || "")
        .replace(/\s+/g, "")
        .toUpperCase() as IbanString;
      return toFullString(createIban(cleanIban, undefined, search));
    }

    if (
      fields.targetType === PaytoType.TalerBank ||
      fields.targetType === "x-taler-bank"
    ) {
      const hostStr = (fields.bankHost || "")
        .trim()
        .replace(/^https?:\/\//, "");
      const host = parseHostPortPath2(hostStr, "") || (hostStr as HostPortPath);
      const acc = (fields.accountName || "").trim();
      return toFullString(createTalerBank(host, acc, search));
    }

    return toFullString(
      createUnsupported(fields.targetType, fields.accountName || "", search),
    );
  }
}

export function codecForPaytoHash(): Codec<PaytoHash> {
  return {
    decode(x: any, c?: Context): PaytoHash {
      // TODO: implement a stronger validation and reject invalid hash
      // maybe check charset and also length
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      return x as PaytoHash;
    },
  };
}

export function codecFullForPaytoString(): Codec<Paytos.FullPaytoString> {
  return {
    decode(x: any, c?: Context): Paytos.FullPaytoString {
      // TODO: implement a stronger validation and reject invalid paytos
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (!x.startsWith(PAYTO_PREFIX)) {
        throw new DecodingError(
          `expected start with payto at ${renderContext(c)} but got "${x}"`,
        );
      }
      return x as Paytos.FullPaytoString;
    },
  };
}

/**
 * @deprecated use codecForNormalizedPAyto or codecForFullPayto
 * @returns
 */
export function codecForPaytoString(): Codec<PaytoString> {
  return {
    decode(x: any, c?: Context): PaytoString {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (!x.startsWith(PAYTO_PREFIX)) {
        throw new DecodingError(
          `expected start with payto at ${renderContext(c)} but got "${x}"`,
        );
      }
      return x as PaytoString;
    },
  };
}

export interface WellKnownPaytoParams {
  amount?: AmountString;
  message?: string;
  ["ch-qrr"]?: string;
  ["receiver-name"]?: string;
  ["receiver-town"]?: string;
  ["receiver-postal-code"]?: string;
}

/**
 * Add query parameters to a payto URI.
 *
 * Existing parameters are preserved.
 */
export function addPaytoQueryParams(
  s: string,
  params: WellKnownPaytoParams,
): string {
  if (!s.startsWith(PAYTO_PREFIX)) {
    throw Error("not a payto URI");
  }
  const [acct, search] = s.slice(PAYTO_PREFIX.length).split("?");
  const searchParams = new URLSearchParams(search || "");
  for (const [paramKey, paramValue] of Object.entries(params)) {
    searchParams.set(paramKey, paramValue);
  }
  const paramList = [...searchParams.entries()];
  if (paramList.length === 0) {
    return PAYTO_PREFIX + acct;
  }
  return PAYTO_PREFIX + acct + "?" + createSearchParams(paramList);
}

/**
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_rfc3986
 */
function encodeRFC3986URIComponent(str: string): string {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}
const rfc3986 = encodeRFC3986URIComponent;

/**
 *
 * https://www.rfc-editor.org/rfc/rfc3986
 */
function createSearchParams(paramList: [string, string][]): string {
  return paramList
    .map(([key, value]) => `${rfc3986(key)}=${rfc3986(value)}`)
    .join("&");
}

export function paytoFromTransferSubject(
  basePayto: string,
  opt: TransferSubject,
): string | undefined {
  switch (opt.type) {
    case "SIMPLE": {
      return addPaytoQueryParams(basePayto, {
        message: opt.subject,
        amount: opt.credit_amount,
      });
    }
    case "URI": {
      return undefined;
    }
    case "CH_QR_BILL": {
      return addPaytoQueryParams(basePayto, {
        "ch-qrr": opt.qr_reference_number,
        amount: opt.credit_amount,
      });
    }
    default:
      logger.warn(`Unsupported transfer subject: ${j2s(opt)}`);
      return undefined;
  }
}
